/**
 * Employee field-level change history (4.36.1, Part A).
 *
 * Reads the audit trail for one employee from GET /employees/:id/history.
 * Sensitive values (SSN, banking, W-4 / state withholding amounts) are masked
 * server-side — the tab shows that a change happened, never the figures.
 */
import React, { useEffect, useState } from 'react';
import { employeesAPI } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { History, Loader2, ShieldAlert } from 'lucide-react';

function fmtWhen(ts) {
  if (!ts) return '';
  try { return new Date(ts).toLocaleString(); } catch { return String(ts); }
}

function fmtVal(v) {
  if (v === null || v === undefined || v === '') {
    return <span className="text-slate-400">(empty)</span>;
  }
  if (typeof v === 'object') return <span className="text-slate-400">(details hidden)</span>;
  return <span className="font-mono text-xs">{String(v)}</span>;
}

const ACTION_STYLES = {
  CREATE: 'bg-green-100 text-green-800',
  UPDATE: 'bg-blue-100 text-blue-800',
  DELETE: 'bg-red-100 text-red-800',
  MERGE: 'bg-amber-100 text-amber-800',
  EMPLOYEE_MERGE: 'bg-amber-100 text-amber-800',
};

export default function EmployeeHistoryTab({ employeeId }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);

  useEffect(() => {
    let active = true;
    setLoading(true);
    employeesAPI.history(employeeId)
      .then((res) => { if (active) { setData(res.data); setError(null); } })
      .catch((e) => { if (active) setError(e?.response?.data?.detail || 'Could not load history'); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [employeeId]);

  if (loading) {
    return (
      <div className="flex items-center gap-2 p-6 text-slate-500" data-testid="history-loading">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading history…
      </div>
    );
  }
  if (error) {
    return (
      <Card data-testid="history-error">
        <CardContent className="flex items-center gap-2 p-6 text-sm text-red-600">
          <ShieldAlert className="h-4 w-4" /> {error}
        </CardContent>
      </Card>
    );
  }

  const rows = data?.history || [];

  return (
    <Card data-testid="employee-history-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          <History className="h-5 w-5" /> Change History
          <Badge variant="secondary" data-testid="history-count">{data?.count || 0}</Badge>
        </CardTitle>
        <p className="text-sm text-slate-500">
          Every recorded change to this employee. Sensitive values (SSN, banking,
          tax withholding amounts) are hidden — only that a change occurred is shown.
        </p>
      </CardHeader>
      <CardContent>
        {data?.employee?.merged_into && (
          <div className="mb-4 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800" data-testid="history-merged-banner">
            This record was merged into employee #{data.employee.merged_into_number || '—'}.
          </div>
        )}
        {rows.length === 0 ? (
          <p className="py-6 text-center text-sm text-slate-400" data-testid="history-empty">
            No change history recorded yet.
          </p>
        ) : (
          <ol className="space-y-3" data-testid="history-list">
            {rows.map((r, i) => {
              const changes = Array.isArray(r.changes) ? r.changes : null;
              const fields = changes ? [] : Object.entries(r.details || {}).filter(([k]) => k !== 'actor');
              return (
                <li key={i} className="rounded-lg border border-slate-200 p-3 dark:border-slate-700" data-testid={`history-row-${i}`}>
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge className={ACTION_STYLES[r.action] || 'bg-slate-100 text-slate-700'}>{r.action}</Badge>
                    <span className="text-xs text-slate-500">{fmtWhen(r.timestamp)}</span>
                    {r.actor?.name && (
                      <span className="text-xs text-slate-600 dark:text-slate-300">by {r.actor.name}</span>
                    )}
                  </div>
                  {changes && (
                    changes.length === 0 ? (
                      <p className="mt-2 text-xs text-slate-400" data-testid={`history-nochange-${i}`}>Saved with no field changes.</p>
                    ) : (
                      <ul className="mt-2 space-y-1" data-testid={`history-changes-${i}`}>
                        {changes.map((c) => (
                          <li key={c.field} className="flex flex-wrap items-baseline gap-2 text-sm">
                            <span className="font-medium text-slate-700 dark:text-slate-200">{c.label}:</span>
                            {c.sensitive ? (
                              <span className="text-slate-400">••• (hidden)</span>
                            ) : (
                              <span className="inline-flex flex-wrap items-center gap-1.5">
                                {fmtVal(c.old)}
                                <span className="text-slate-400">→</span>
                                <span className="font-semibold">{fmtVal(c.new)}</span>
                              </span>
                            )}
                          </li>
                        ))}
                      </ul>
                    )
                  )}
                  {!changes && fields.length > 0 && (
                    <dl className="mt-2 grid grid-cols-1 gap-1 sm:grid-cols-2">
                      {fields.map(([k, v]) => (
                        <div key={k} className="flex items-baseline gap-2 text-sm">
                          <dt className="min-w-0 shrink-0 font-medium text-slate-600 dark:text-slate-300">{k}:</dt>
                          <dd className="min-w-0 break-words">{fmtVal(v)}</dd>
                        </div>
                      ))}
                    </dl>
                  )}
                </li>
              );
            })}
          </ol>
        )}
      </CardContent>
    </Card>
  );
}
